import React, { useEffect, useState, useRef } from "react";
import { connect, useDispatch, useSelector } from 'react-redux'
import { reduxForm } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Screen, Utils } from '../../../../services';
import { ItemsList, LanguageSelector } from '../../../../components';
import { Form, Row, Col, Button, ButtonGroup, Card, CardBody, CardText, CardTitle, Input, Label } from "reactstrap";
import _ from "lodash";

import { Setting, PdfLayout } from '../../../../models';
import { PdfLayoutOptions } from './PdfLayoutOptions';
import { PdfPageOptions } from './PdfPageOptions';
import { PdfWidgetOptions } from './PdfWidgetOptions';
import { PdfWidgetsBrowser } from './PdfWidgetsBrowser';
import { DragAndResize } from "./DragAndResize";
import moment from 'moment';

import { resetLayouts, loadLayout, loadLayouts, updateLayout, createLayout, duplicateLayout, deleteLayout } from '../../../../redux';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';

import './PdfLayouts.css';

const initialState = widgets => ({
    past: [],
    present: [...(widgets || [])],
    future: []
});

let PdfLayouts = (props) => {
    const { handleSubmit, submitting } = props;

    const { layouts, layout, isLoadingOne, loadingErrorMessage } = useSelector(state => state.layouts);

    const { programmations }  = useSelector(state => state.programmations);
    const { pricinglists }    = useSelector(state => state.pricinglists);
    const { screenings }      = useSelector(state => state.screenings);
    const { places }          = useSelector(state => state.places);
    const { tickettypes }     = useSelector(state => state.tickettypes);
    const { fonts }           = useSelector(state => state.fonts);
    const { setting }         = useSelector(state => state.settings);

    const dispatch     = useDispatch();
    const { t, i18n }  = useTranslation();
    const { isMobile } = useSelector(state => state.ui);

    const { layoutId } = useParams();

    const [mainContainerWidth, setMainContainerWidth]   = useState(PdfLayout.sizes('Custom').width);
    const [mainContainerHeight, setMainContainerHeight] = useState(PdfLayout.sizes('Custom').height);
    const [widgets, _setWidgets]                        = useState([]);
    const [inEdition, setInEdition]                     = useState(false);

    const [currentPageIndex, setCurrentPageIndex]       = useState(0);
    const [currentPage, setCurrentPage]                 = useState(undefined);
    const [currentWidget, setCurrentWidget]             = useState(undefined);
    const [currentOptionsGroup, setCurrentOptionsGroup] = useState('position');
    const [previewLanguage, setPreviewLanguage]         = useState(i18n.language);
    const [scale, setScale]                             = useState(1);

    const [displayPrice, setDisplayPrice]   = useState(false);
    const [programmation, setProgrammation] = useState(false);
    const [place, setPlace]                 = useState(false);
    const [tickettype, setTickettype]       = useState(false);
    const [pricinglist, setPricinglist]     = useState(false);
    const [priceSelected, setPriceSelected] = useState(false);
    const [oneTimePass, setOneTimePass]     = useState(false);

    // layout remove confirmation
    const [confirmationIsVisible, setConfirmationVisible] = useState(false);

    const [ state, setState ] = useState(initialState([]));

    const pushState = newWidgets => {
        if (_.isEqual(newWidgets, state?.present))
            return;

        const past    = [...state.past, state.present];
        const present = [ ...newWidgets ];
        const future  = [];
        setState({ past, present, future });
    }
    const undo = () => {
        if (!state.past.length)
            return;

        const present = state.past[state.past.length - 1];
        const past    = state.past.slice(0, state.past.length - 1);
        const future  = [ state.present, ...state.future];
        setState({ past, present, future });
        setWidgets(present, /*ignoreState*/true);

        const current = present.find(w => w?.id === currentWidget?.id);
        setCurrentWidget(undefined);
        setTimeout(() => setCurrentWidget(current), 0);
    }
    const redo = () => {
        if (!state.future.length)
            return;
        const present = state.future[0];
        const future  = state.future.slice(1);
        const past    = [...state.past, state.present];
        setState({ past, present, future });
        setWidgets(present, /*ignoreState*/true);
        const current = present.find(w => w?.id === currentWidget?.id);
        setCurrentWidget(undefined);
        setTimeout(() => setCurrentWidget(current), 0);
    }

    const setWidgets = (widgets, ignoreState, resetState) => {
        if (resetState)
            setState(initialState(widgets));
        else if (!ignoreState && widgets?.length > 0)
            pushState([...widgets]);
        return _setWidgets(widgets);
    };

    const onKeyDown = React.useCallback(e => {
        if (!e.ctrlKey)
            return;

        switch (e.key) {
            case 'z':
                e.preventDefault();
                e.stopPropagation();
                return undo();
            case 'y':
                e.preventDefault();
                e.stopPropagation();
                return redo();
        }
    });

    useEffect(() => {
        dispatch(resetLayouts());
        dispatch(loadLayouts());
    }, [])

    useEffect(() => {
        if (layoutId)
            dispatch(loadLayout(layoutId));
        setCurrentPageIndex(0);
    }, [dispatch, layoutId])

    useEffect(() => {
        if (layout && layout.layout_type === 'pdf') {
            // let's ensure that all the widgets are instances of PdfLayoutWidget
            layout.widgets = layout.widgets.map(w => PdfLayout.createWidget(w));
            setWidgets(layout.widgets.map(w => w.toPlainObject()), /*ignoreState*/true, /*resetState*/true);
            setCurrentPage(layout.pages[currentPageIndex || 0]);
        }
    }, [layout])

    useEffect(() => {
        if (layout?.pages?.length > currentPageIndex)
            setCurrentPage(layout.pages[currentPageIndex]);
    }, [currentPageIndex])

    useEffect(() => {
        Screen.setScale(scale);
    }, [scale]);

    useEffect(() => {
        if (!layout || !widgets)
            return;

        layout.widgets = [...widgets];
        //dispatch(updateLayout(layout));
    }, [widgets]);

    useEffect(() => {
        if (!fonts || !fonts.length)
            return;

        fonts.forEach(font => {
            loadFont(font.name, font.url);
            Object.keys(font.variants).map(key => {
                const variant = font.variants[key];
                variant.url && loadFont(variant.name, variant.url);
            });
        });
    }, [fonts]);

    const loadFont = async (name, url) => {
        const fontFace = new FontFace(name, `url(${url.replaceAll(' ', '%20')})`, {});
        // wait for font to be loaded
        try {
            await fontFace.load();
            // add font to document
            document.fonts.add(fontFace);
            // enable font with CSS class
            document.body.classList.add("fonts-loaded");
        } catch (e) {
            console.error('Could not load font', name, url, e);
        }
    }

    const addLayout = () => dispatch(createLayout(PdfLayout.defaultLayout()));

    const addPage = async () => {
        if (!layout)
            return;

        layout.pages   = layout.pages || {};
        const nbPages  = layout.pages.length;
        const lastPage = nbPages > 0 ? layout.pages[nbPages - 1] : {};
        layout.pages.push({
            size: lastPage.size || 'A4',
            dimensions: lastPage.dimensions || {
                width: 21,
                height: 29.7
            }
        });
        dispatch(updateLayout(layout, (err, updated) => {
            if (!err)
                setCurrentPageIndex(nbPages);
        }));
    }

    const duplicatePage = () => {
        if (!layout || !currentPage)
            return;

        layout.pages.push(_.cloneDeep(currentPage));

        const newWidgets = _.cloneDeep(layout.widgets.filter(w => w.page === currentPageIndex))
            .map(w => {
                const { id, ...rest } = JSON.parse(JSON.stringify(w));
                return {
                    ...rest,
                    page: layout.pages.length - 1,
                };
            });

        layout.widgets = layout.widgets.concat(newWidgets);
        dispatch(updateLayout(layout, (err, updated) => {
            if (!err)
                setCurrentPageIndex(layout.pages.length - 1);
        }));
    };

    const deletePage = () => {
        if (!layout || !currentPage)
            return;

        layout.pages = layout.pages
            .filter((p, i) => i !== currentPageIndex);

        layout.widgets = layout.widgets
            .filter(w => w.page !== currentPageIndex)
            .map(w => {
                if (w.page > currentPageIndex)
                    w.page --;
                return w;
            });

        dispatch(updateLayout(layout, (err, updated) => {
            if (err)
                return dispatch(loadLayout(layout._id));

            setCurrentPageIndex(0);
        }));
    };

    const duplicate = async () => {
        await dispatch(duplicateLayout(layout));
        dispatch(loadLayouts());
    }

    const removeLayout = () => {
        // reset current layout
        dispatch(deleteLayout(layout?._id));
        setCurrentWidget(undefined);
        dispatch(loadLayouts());
        setConfirmationVisible(false);
    }

    const addWidget = (type, subType) => {
        if (!layout)
            return;

        const widget = PdfLayout.createWidget({ type, subType, page: currentPageIndex });
        layout.widgets.push(widget);
        dispatch(updateLayout(layout));
        if (widget.isEditable())
            setTimeout(() => setCurrentWidget(widget), 200);
    };

    const duplicateWidget = () => {
        if (!layout)
            return;

        const newWidget = _.cloneDeep(currentWidget);
        delete newWidget.id;

        newWidget.position = newWidget.position || {x: 0, y: 0, z: 0};
        newWidget.position.x += 1;
        newWidget.position.y += 1;
        layout.widgets.push(newWidget);
        dispatch(updateLayout(layout));
        setTimeout(() => setCurrentWidget(newWidget), 200);
    };

    const deleteWidget = () => {
        if (!layout || !currentWidget)
            return;

        layout.widgets = layout.widgets.filter(w => w.id !== currentWidget.id);
        dispatch(updateLayout(layout));
        setCurrentWidget(undefined);
    };

    const moveWidgetBackward = () => {
        if (!currentWidget)
            return;

        const newWidgets = [currentWidget].concat(
            widgets.filter(w => w.id != currentWidget.id)
        );
        setWidgets(newWidgets);
        setCurrentWidget(newWidgets[0]);
    };

    const moveWidgetForward = () => {
        if (!currentWidget)
            return;

        const newWidgets = widgets.filter(w => w.id != currentWidget.id).concat([
            currentWidget
        ]);
        setWidgets(newWidgets);
        setCurrentWidget(newWidgets[newWidgets.length - 1]);
    };

    const handleChangeFromDragAndResize = (widget, index) => {
        const newWidgets = _.cloneDeep(widgets);
        newWidgets[index] = {
            ...newWidgets[index],
            position: widget.position,
            dimensions: widget.dimensions,
            style: widget.style,
        };
        setWidgets(newWidgets);
        setCurrentWidget(widget);
    };

    const handleNewOptions = (widget) => {
        const newWidgets = _.cloneDeep(widgets).map(w => {
            if (w.id === widget.id)
                return { ...widget };
            return w;
        });;
        setWidgets(newWidgets);
    };

    const selectImage = (event, index) => {
        const file = event.target.files[0];
        const fileReader = new FileReader()
        fileReader.onloadend = () => {
            const newWidgets = widgets;
            const widget = newWidgets[index];
            widget.options.rfc2397_data = fileReader.result;
            newWidgets[index] = widget;
            setWidgets([...newWidgets]);
        }
        fileReader.readAsDataURL(file)
    };

    const setPdfPageSize = ({ size, width, height }) => {
        if (!layout)
            return;

        if (size)
            layout.pages[currentPageIndex].size = size;

        if (size && (size !== 'Custom')) {
            width  = PdfLayout.sizes(size).width;
            height = PdfLayout.sizes(size).height;
        }

        if (width)
            layout.pages[currentPageIndex].dimensions.width = width;
        if (height)
            layout.pages[currentPageIndex].dimensions.height = height;

        dispatch(updateLayout(layout));
    }

    const loadOverrideTickettypes = (id) => {
        const tickettype = id ? tickettypes.find(t => t._id === id) : false;
        setTickettype(tickettype);
        setPricinglist(tickettype.pricings);
        setPriceSelected(false);
        setDisplayPrice(true);
        if (id === "one-time-pass") {
            setOneTimePass(true);

        } else {
            setOneTimePass(false);
            setProgrammation(false);
            setPlace(false);
        }
    }
    const loadOverridePrice = (id) => {
        if (id.length > 0) {
            if (id in pricinglist)
                setPriceSelected(pricinglist[id]);
        } else {
            setProgrammation(false);
            setPlace(false);
        }
    }

    const loadOverridePricingList = (ids) => {
        const [pricingListId, pricingId] = ids.split(":");

        const pricinglist = pricinglists.find((p) => p._id == pricingListId);
        if (!pricinglist)
            return;

        const price = pricinglist.pricings[pricingId];

        if (price) {
            setPriceSelected(price);
        } else {
            setProgrammation(false);
            setPlace(false);
        }
    }
    const loadOverrideEvents = (id) => {
        if (id.length > 0) {
            const prog  = id ? programmations.find(p => p.id === id) : false;
            const event = id ? screenings.find(s => s._id === id) : false;

            if (prog) {
                const place = id ? places.find(p => p._id === prog.place_id) : false;
                setProgrammation(prog);
                setPlace(place);
            }
            if (event) {
                const place = id ? places.find(p => p._id === event.cinema_hall._id) : false;
                setProgrammation(event);
                setPlace(place);
            }
        } else {
            setProgrammation(false);
            setPlace(false);
        }
    }

    const history = useHistory();
    const onSelectLayout = (_id) => {
        setCurrentWidget(undefined);

        if (_id === '__add')
            return addLayout();

        history.push(`/layouts/edit/${_id}`);
    }

    const goToTicketType = (tickettypeId, index) => {
        if (tickettypeId) {
            history.push(`/tickettypes/edit/${tickettypeId}`);
        }
    }

    const pdfLinkedToTicketTypes = (tickettypes || []).filter(tickettype => {
        return layout && (tickettype.opaque?.layouts?.length > 0 && tickettype.opaque.layouts[layout.target] === layout._id);
    }).map((data, index) => ({
        key: data._id,
        title: data.name[i18n.language],
        selection: data._id,
        depth: 0,
        icon: 'tags'
    }));

    const selectWidget = (widget, index) => {
        setCurrentWidget(undefined);
        // we let the options widget the time to reset
        setTimeout(() => {
            if (widget.isEditable())
                setCurrentWidget(widget);
        }, 0);
    }

    const createGrid = () => {
        if (!currentPage)
            return null;

        const { width, height } = currentPage.dimensions;
        const gridArray = []
        for (let index = 0.5; index < width; index += 0.5) {
            gridArray.push(<div key={`x-${index}`} style={{ left: `${Screen.cmToPx(index)}px`, y: 0, height: `${Screen.cmToPx(height)}px`, width: '1px', backgroundColor: '#E5E5E5', position: 'absolute' }} />)
            if ((index * 10) % 10 === 0)
                gridArray.push(<div key={`xx-${index}`} style={{ left: `${Screen.cmToPx(index)}px`, y: 0, height: `${Screen.cmToPx(0.4)}px`, width: '1px', backgroundColor: '#858585', position: 'absolute' }} />)
            else
                gridArray.push(<div key={`xx-${index}`} style={{ left: `${Screen.cmToPx(index)}px`, y: 0, height: `${Screen.cmToPx(0.1)}px`, width: '1px', backgroundColor: '#858585', position: 'absolute' }} />)
        }

        for (let index = 0.5; index < height; index += 0.5) {
            gridArray.push(<div key={`y-${index}`} style={{ x: 0, top: `${Screen.cmToPx(index)}px`, height: '1px', width: `${Screen.cmToPx(width)}px`, backgroundColor: '#E5E5E5', position: 'absolute' }} />)
            if ((index * 10) % 10 === 0)
                gridArray.push(<div key={`yy-${index}`} style={{ x: 0, top: `${Screen.cmToPx(index)}px`, height: '1px', width: `${Screen.cmToPx(0.4)}px`, backgroundColor: '#858585', position: 'absolute' }} />)
            else
                gridArray.push(<div key={`yy-${index}`} style={{ x: 0, top: `${Screen.cmToPx(index)}px`, height: '1px', width: `${Screen.cmToPx(0.1)}px`, backgroundColor: '#858585', position: 'absolute' }} />)
        }

        return gridArray
    }

    const items = (layouts || []).filter(l => l.layout_type !== 'email').map((l, index) => {
        return {
            key: `${l.name[i18n.language]}.${index}`,
            title: l.name[i18n.language],
            icon: isLoadingOne && l._id === layoutId ? 'spinner fa-spin' : 'th',
            active: l._id === layout?._id ? true : false,
            depth: 0,
            selection: l._id
        }
    });
    items.push({
        key: '__add',
        selection: '__add',
        title: t("layouts.add_new_layout"),
        icon: 'plus',
        isButton: true,
        depth: 0
    });

    const icons = {
        'text_owner_info'      : 'user',
        'text_ticket_info'     : 'tag',
        'text_tickettype_info' : 'tags',
        'text_screening_info'  : 'film',
        'text_customer_info'   : 'gear',
        'text_environment_info': 'globe',
        'text_custom'          : 'commenting-o',
        'text_link'            : 'link',
        'image_library'        : 'photo',
        'image_custom'         : 'photo',
        'image_owner_contact'  : 'user',
        'image_qrcode'         : 'qrcode',
        'shape_frame'          : 'square-o',
        'shape_line'           : 'arrows-h'
    }

    const widgetsList = React.useMemo(() => {
        return (widgets || []).filter(w => w.page === currentPageIndex).slice().reverse().map((w, index) => {
            const key = (widgets || []).length - index - 1;
            return {
                key: index,
                title: t(`layouts.${w.type}_${w.subType}`),
                icon: icons[w.type + '_' + w.subType],
                active: w.id === currentWidget?.id,
                depth: 0,
                selection: PdfLayout.createWidget(w)
            }
        });
    }, [widgets, currentWidget, currentPageIndex]);

    return (
        <div className="PdfLayouts" onKeyDown={ onKeyDown }>
            {loadingErrorMessage &&
                <div
                    className="alert alert-danger"
                    dangerouslySetInnerHTML={{__html: loadingErrorMessage}} />
            }
            <Card>
                <CardBody>
                        <CardText tag="div">
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col className="post_categories-browser" sm={2}>
                                        <div className="TypeSelector">
                                            <ItemsList
                                                title={t('layouts.layouts')}
                                                items={items}
                                                onSelect={onSelectLayout}
                                            />
                                        </div>
                                        {layout &&
                                            <>
                                                <Card className="h-auto mb-3">
                                                    <CardBody>
                                                        <CardTitle className="small">{t('settings.delivery.type.general_informations')}</CardTitle>
                                                        <CardText tag="div">
                                                            <PdfLayoutOptions />
                                                            {!confirmationIsVisible && (
                                                            <>
                                                                <Row>
                                                                    <Col>

                                                                        <ButtonGroup>
                                                                            <Button onClick={() => duplicate()} color='info' size="sm">
                                                                                <i className="fa fa-copy"></i> {t("common.duplicate")}
                                                                            </Button>
                                                                            <Button color="danger" onClick={() => setConfirmationVisible(true)}>
                                                                                <span>
                                                                                    <i className="fa fa-trash"></i>&nbsp;{t("common.delete")}
                                                                                </span>
                                                                            </Button>
                                                                        </ButtonGroup>
                                                                    </Col>
                                                                </Row>
                                                                <hr />
                                                                <Row>
                                                                    <Col>

                                                                        <ButtonGroup>
                                                                            <Button onClick={handleSubmit} type="submit" color="success" disabled={submitting || isLoadingOne}>
                                                                                {isLoadingOne ?
                                                                                    <span><i className="fa fa-spinner fa-spin"></i></span>
                                                                                    :
                                                                                    <span><i className="fa fa-save"></i>&nbsp; {t("common.save")} </span>
                                                                                }
                                                                            </Button>
                                                                        </ButtonGroup>
                                                                    </Col>
                                                                </Row>
                                                            </>
                                                            )}
                                                            {confirmationIsVisible && (
                                                            <Row>
                                                                <Col>
                                                                    <b>{t('common.are_you_sure')}</b>&nbsp;
                                                                    <ButtonGroup>
                                                                        <Button onClick={() => setConfirmationVisible(false)} color='info' size="sm">
                                                                            <i className="fa fa-times"></i> {t("common.no")}
                                                                        </Button>
                                                                        <Button type="submit" color="danger" onClick={removeLayout}>
                                                                            <span>
                                                                                <i className="fa fa-trash"></i>&nbsp;{t("common.yes")}
                                                                            </span>
                                                                        </Button>
                                                                    </ButtonGroup>
                                                                </Col>
                                                            </Row>
                                                            )}
                                                        </CardText>
                                                    </CardBody>
                                                </Card>
                                            </>
                                        }
                                        {pdfLinkedToTicketTypes.length > 0 &&
                                            <div className="TypeSelector pdfLinkedToTicketTypes">
                                                <ItemsList
                                                    title={t('settings.pdf.layout_link')}
                                                    items={pdfLinkedToTicketTypes}
                                                    onSelect={goToTicketType}
                                                />
                                            </div>
                                        }
                                    </Col>
                                    <Col className="post_categories-browser" sm={10}>
                                        {layout &&
                                            <>
                                                <Row>
                                                    <Col sm={inEdition ? 8 : 12}>
                                                        <div className={"topButtonsWrapper " + (isMobile ? "w-100" : "")}>
                                                            <ButtonGroup className={isMobile ? "w-100" : ""}>
                                                                <Button onClick={() => setInEdition(true)} color={inEdition ? 'primary' : 'dark'} size="sm">
                                                                    <i className="fa fa-pencil"></i> {t("settings.pdf.edit_mode")}
                                                                </Button>
                                                                <Button onClick={() => setInEdition(false)} color={!inEdition ? 'primary' : 'dark'} size="sm">
                                                                    <i className="fa fa-eye"></i> {t("settings.pdf.preview_mode.title")}
                                                                </Button>
                                                            </ButtonGroup>

                                                            <LanguageSelector onChange={lang => setPreviewLanguage(lang)} />

                                                            <div className={"rangeWrapper " + (!isMobile ? "pull-right w-50" : "w-100")}>
                                                                <div className="range w-100">
                                                                    <i className="fa fa-search-minus" />
                                                                    <Input
                                                                        type="range"
                                                                        min={0.25}
                                                                        max={3}
                                                                        step={0.05}
                                                                        value={scale}
                                                                        onChange={e => setScale(e.target.value)}
                                                                    />
                                                                    <i className="fa fa-search-plus" />
                                                                </div>
                                                                {!isMobile &&
                                                                    <div className="rangeInfos">
                                                                        <Input
                                                                            type="number"
                                                                            min={0.25}
                                                                            max={3}
                                                                            step={0.05}
                                                                            value={parseInt(scale * 100)}
                                                                            onChange={e => setScale(e.target.value / 100)}
                                                                        />
                                                                        %
                                                                    </div>
                                                                }
                                                            </div>
                                                        </div>
                                                        {!inEdition &&
                                                            // Tickettypes list
                                                            <div className={"dataTestButtonsWrapper d-flex"}>
                                                                <Label className="mt-2">{t("settings.pdf.preview_mode.data_test")}</Label>
                                                                <Input type="select" className="w-25 ml-3" onChange={e => loadOverrideTickettypes(e.target.value)}>
                                                                    <option value="">{t("settings.pdf.preview_mode.choose_tickettypes_list")}</option>
                                                                    {tickettypes && tickettypes.map(field => (
                                                                        <option key={field._id} value={field._id}>
                                                                                {Utils.localized_or_fallback(field.name, previewLanguage)}
                                                                        </option>
                                                                    ))}
                                                                </Input>

                                                                {!oneTimePass && displayPrice && pricinglist &&
                                                                    // Pricinglist in tickettype
                                                                    <Input type="select" className="w-25 ml-3" onChange={e => loadOverridePrice(e.target.value)}>
                                                                        <option value="">{t("settings.pdf.preview_mode.choose_price")}</option>
                                                                        {Object.keys(pricinglist).map((key, index) => (
                                                                            <option key={index} value={key}>
                                                                                {Utils.localized_or_fallback(pricinglist[key].name, previewLanguage)}  - {pricinglist[key].price.CHF} {Object.keys(pricinglist[key].price)}
                                                                            </option>
                                                                        ))}
                                                                    </Input>
                                                                }
                                                                {oneTimePass &&
                                                                <>
                                                                    {/* All Pricinglists */}
                                                                    <Input type="select" className="w-25 ml-3" onChange={e => loadOverridePricingList(e.target.value)}>
                                                                        <option value="">{t("settings.pdf.preview_mode.choose_price")}</option>
                                                                        {pricinglists.filter(l => Object.keys(l.pricings).length > 0).map(list => (
                                                                            <optgroup key={list._id} label={list.name}>
                                                                                {Object.keys(list.pricings).map((key, index) => (
                                                                                <option
                                                                                    key={Utils.localized_or_fallback(list.pricings[key].name, previewLanguage)}
                                                                                    value={list._id +':'+ key}
                                                                                >
                                                                                    {Utils.localized_or_fallback(list.pricings[key].name, previewLanguage)} - {list.pricings[key].price.CHF} {Object.keys(list.pricings[key].price)}
                                                                                </option>
                                                                                ))}
                                                                            </optgroup>
                                                                        ))}
                                                                    </Input>
                                                                    <Input type="select" className="w-auto ml-3" onChange={e => loadOverrideEvents(e.target.value)}>
                                                                        <option value="">{t("settings.pdf.preview_mode.choose_movies_list")}</option>
                                                                        {screenings && screenings.length > 0 && screenings.map((screening, index) => (
                                                                            <option key={ index } value={screening?._id}>
                                                                                {Utils.localized_or_fallback(screening.title, previewLanguage) +' - '+ moment(screening.start_at).format('LT l')}
                                                                            </option>
                                                                        ))}
                                                                        {programmations && programmations.map(programmation => (
                                                                            <option key={programmation.id} value={programmation.id}>
                                                                                {Utils.localized_or_fallback(programmation.title, previewLanguage) +' - '+ moment(programmation.start_at).format('LT l')}
                                                                            </option>
                                                                        ))}
                                                                    </Input>
                                                                </>
                                                                }
                                                            </div>
                                                        }
                                                        {inEdition && (
                                                            <div className="pagesNavigatorWrapper d-flex justify-between">
                                                                <ButtonGroup className={isMobile ? "w-100" : ""}>
                                                                    { layout?.pages?.map((page, i) => (
                                                                        <Button key={ i } onClick={() => setCurrentPageIndex(i)} color={currentPageIndex === i ? 'primary' : 'dark'} size="sm">
                                                                            <i className="fa fa-file-o"></i>&nbsp;&nbsp;{t("layouts.page")} { i + 1 }
                                                                        </Button>
                                                                    ))}
                                                                    <Button onClick={() => addPage()} color="info" size="sm">
                                                                        <i className="fa fa-plus"></i> {t("layouts.add_page")}
                                                                    </Button>
                                                                </ButtonGroup>
                                                                {(state.past?.length > 0 || state.future?.length > 0) && (
                                                                <ButtonGroup className={isMobile ? "w-100" : ""}>
                                                                    <Button onClick={ undo } color={state.past.length > 0 ? 'info' : 'dark'} size="sm" disabled={!state.past}>
                                                                        <i className="fa fa-undo"></i>
                                                                    </Button>
                                                                    <Button onClick={ redo } color={state.future.length > 0 ? 'info' : 'dark'} size="sm" disabled={!state.future}>
                                                                        <i className="fa fa-repeat"></i>
                                                                    </Button>
                                                                </ButtonGroup>
                                                                )}
                                                            </div>
                                                        )}
                                                        <div className={"previewWrapper " + (isMobile ? "w-100" : "")}>
                                                            {layout?.pages?.filter((p, i) => !inEdition || i === currentPageIndex).map((page, pageIndex) => (
                                                                <div key={ pageIndex } className="preview" style={{
                                                                    width: `${Screen.cmToPx(page.dimensions.width)}px`,
                                                                    height: `${Screen.cmToPx(page.dimensions.height)}px`
                                                                }}>
                                                                    {inEdition && (
                                                                        <div style={{ width: '100%', height: '100%' }}>
                                                                            {createGrid()}
                                                                        </div>
                                                                    )}

                                                                    {widgets.map((data, index) => {
                                                                        const p = inEdition ? currentPageIndex : pageIndex;
                                                                        if (data.page !== p &&
                                                                            (data.page !== 'all') &&
                                                                            (data.page !== 'odd' || (p % 2 !== 1)) &&
                                                                            (data.page !== 'even' || (p % 2 !== 0))
                                                                        )
                                                                            return null;

                                                                        const widget = PdfLayout.createWidget(data);
                                                                        return (
                                                                            <DragAndResize
                                                                                key={index}
                                                                                widget={widget}
                                                                                active={widget.id === currentWidget?.id}
                                                                                inEdition={inEdition}
                                                                                lang={previewLanguage}
                                                                                fonts={fonts}
                                                                                setting={new Setting(setting)}
                                                                                onClick={() => selectWidget(widget, index)}
                                                                                onDelete={deleteWidget}
                                                                                onChange={(data) => handleChangeFromDragAndResize(data, index)}
                                                                                onUndo={ undo }
                                                                                onRedo={ redo }
                                                                                programmation={programmation}
                                                                                place={place}
                                                                                tickettype={tickettype}
                                                                                pricing={priceSelected}
                                                                            />
                                                                        );
                                                                    })}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </Col>
                                                    <Col sm={4}>
                                                        {inEdition && widgets.length > 0 &&
                                                        <>
                                                            <CardTitle className="small">{t('layouts.list_widget')}</CardTitle>
                                                            <div className="widgetsLists">
                                                                    <ItemsList
                                                                    items={widgetsList}
                                                                    onSelect={(widget, index) => selectWidget(widget, widgets.length - 1 - index)}
                                                                />
                                                            </div>
                                                        </>
                                                        }
                                                        {inEdition && currentWidget && (
                                                        <>
                                                            <CardTitle className="small mt-3">
                                                                {t('layouts.edit_widget')} - {t(`layouts.${currentWidget.type}_${currentWidget.subType}`)}
                                                            </CardTitle>
                                                            <PdfWidgetOptions
                                                                nbPages={layout?.pages?.length}
                                                                widget={PdfLayout.createWidget(currentWidget)}
                                                                currentGroup={currentOptionsGroup}
                                                                onGroupChange={setCurrentOptionsGroup}
                                                                onSubmit={handleNewOptions}
                                                                onDuplicate={duplicateWidget}
                                                                onBackward={moveWidgetBackward}
                                                                onForward={moveWidgetForward}
                                                                onDelete={deleteWidget}
                                                                onClose={() => setCurrentWidget(undefined)}
                                                            />
                                                        </>
                                                        )}
                                                        {inEdition && !currentWidget && (
                                                        <>
                                                            <CardTitle className="small mt-3">
                                                                {t('layouts.add_widget')}
                                                            </CardTitle>
                                                            <PdfWidgetsBrowser addWidget={addWidget} />
                                                        </>
                                                        )}
                                                        {inEdition && (
                                                            <>
                                                                <CardTitle className="small mt-3">
                                                                    {t('layouts.page_options')}
                                                                </CardTitle>
                                                                <PdfPageOptions
                                                                    pageIndex={ currentPageIndex }
                                                                    page={ currentPage }
                                                                    setPdfPageSize={setPdfPageSize}
                                                                    onDuplicate={duplicatePage}
                                                                    onDelete={deletePage}
                                                                />
                                                            </>
                                                        )}
                                                    </Col>
                                                </Row>
                                            </>
                                        }
                                    </Col>
                                </Row>
                            </Form>
                        </CardText>
                </CardBody>
            </Card>
        </div>
    );
}

// Decorate with reduxForm(). It will read the initialValues prop provided by connect()
PdfLayouts = reduxForm({
  form: 'layoutForm', // a unique identifier for this form
  enableReinitialize: true
})(PdfLayouts);

PdfLayouts = connect(state => ({ initialValues: state.layouts.layout, }), {})(PdfLayouts);

export default PdfLayouts;
